import {useState} from 'react'
import axios from 'axios'
import {API_URL, SYSTEM_API} from '../config/api.config'

type PowerAction = 'shutdown' | 'restart'

export function usePowerActions() {
    const [confirmAction, setConfirmAction] = useState<null | PowerAction>(null)
    const [inProgress, setInProgress] = useState<null | PowerAction>(null)
    const [shutdownConfirmed, setShutdownConfirmed] = useState(false)

    const waitForBackendRecovery = async () => {
        // Poll health: wait for an outage and then a recovery before reloading.
        const healthUrl = `${API_URL}/api/health`
        let sawOutage = false
        const deadline = Date.now() + 5 * 60 * 1000
        while (Date.now() < deadline) {
            try {
                await axios.get(healthUrl, {timeout: 3000})
                if (sawOutage) {
                    window.location.reload()
                    return
                }
            } catch {
                sawOutage = true
            }
            await new Promise((r) => setTimeout(r, 3000))
        }
    }

    const waitForShutdownConfirmed = async () => {
        // 3 consecutive failures confirm the Pi is off.
        const healthUrl = `${API_URL}/api/health`
        let consecutiveFailures = 0
        const deadline = Date.now() + 3 * 60 * 1000
        while (Date.now() < deadline) {
            try {
                await axios.get(healthUrl, {timeout: 3000})
                consecutiveFailures = 0
            } catch {
                consecutiveFailures++
                if (consecutiveFailures >= 3) {
                    setShutdownConfirmed(true)
                    return
                }
            }
            await new Promise((r) => setTimeout(r, 3000))
        }
        setShutdownConfirmed(true)
    }

    const trigger = async (action: PowerAction) => {
        setConfirmAction(null)
        setShutdownConfirmed(false)
        setInProgress(action)
        try {
            await axios.post(action === 'shutdown' ? SYSTEM_API.SHUTDOWN : SYSTEM_API.RESTART)
            if (action === 'restart') {
                waitForBackendRecovery()
            } else {
                waitForShutdownConfirmed()
            }
        } catch (e) {
            console.error(`Failed to ${action} host`, e)
            setInProgress(null)
        }
    }

    const dialogs = (
        <>
            {confirmAction && (
                <div
                    className='modal fade show d-block'
                    tabIndex={-1}
                    style={{backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1080}}
                    onClick={() => setConfirmAction(null)}
                >
                    <div className='modal-dialog modal-dialog-centered' onClick={(e) => e.stopPropagation()}>
                        <div className='modal-content'>
                            <div className='modal-header'>
                                <h5 className='modal-title'>
                                    {confirmAction === 'shutdown' ? 'Shutdown device?' : 'Restart device?'}
                                </h5>
                                <button
                                    type='button'
                                    className='btn-close'
                                    onClick={() => setConfirmAction(null)}
                                ></button>
                            </div>
                            <div className='modal-body'>
                                {confirmAction === 'shutdown' ? (
                                    <p className='mb-0'>
                                        This will <strong>power off</strong> the Raspberry Pi. To turn it
                                        back on, you will need to <strong>disconnect the power cable and
                                        reconnect it</strong>.
                                    </p>
                                ) : (
                                    <p className='mb-0'>
                                        This will <strong>reboot</strong> the Raspberry Pi. The device will be
                                        unavailable for about a minute.
                                    </p>
                                )}
                            </div>
                            <div className='modal-footer'>
                                <button
                                    type='button'
                                    className='btn btn-light'
                                    onClick={() => setConfirmAction(null)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type='button'
                                    className={`btn ${confirmAction === 'shutdown' ? 'btn-danger' : 'btn-warning'}`}
                                    onClick={() => trigger(confirmAction)}
                                >
                                    {confirmAction === 'shutdown' ? 'Shutdown' : 'Restart'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {inProgress && (
                <div
                    className='modal fade show d-block'
                    tabIndex={-1}
                    style={{backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 1090}}
                >
                    <div className='modal-dialog modal-dialog-centered'>
                        <div className='modal-content'>
                            <div className='modal-body text-center py-10'>
                                {inProgress === 'shutdown' && shutdownConfirmed ? (
                                    <>
                                        <div className='mb-4'>
                                            <i className='bi bi-power text-success' style={{fontSize: '3rem'}}></i>
                                        </div>
                                        <h4 className='mb-2 text-success'>Device powered off</h4>
                                        <p className='text-muted mb-2'>
                                            The Raspberry Pi is now off. You can safely disconnect power.
                                        </p>
                                        <p className='text-muted small mb-0'>
                                            To turn it back on, disconnect the power cable and reconnect it.
                                            This page will no longer respond.
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <div className='spinner-border text-primary mb-4' role='status'>
                                            <span className='visually-hidden'>Working...</span>
                                        </div>
                                        <h4 className='mb-2'>
                                            {inProgress === 'shutdown' ? 'Shutting down...' : 'Restarting...'}
                                        </h4>
                                        <p className='text-muted mb-3'>
                                            {inProgress === 'shutdown'
                                                ? 'Waiting for the device to power off completely...'
                                                : 'The device will be back online in about a minute. This page will reload automatically.'}
                                        </p>
                                        {inProgress === 'restart' && (
                                            <button
                                                type='button'
                                                className='btn btn-sm btn-light-primary'
                                                onClick={() => window.location.reload()}
                                            >
                                                Reload now
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    )

    return {
        requestRestart: () => setConfirmAction('restart'),
        requestShutdown: () => setConfirmAction('shutdown'),
        busy: inProgress !== null,
        dialogs,
    }
}
